import { useState } from "react";
import { Search, Package, ArrowRight } from "lucide-react";
import { setShopFilter } from "@/lib/shopFilter";

const SEASONS = [
  { id: "all", label: "Bármelyik évszak" },
  { id: "nyari", label: "Nyári" },
  { id: "teli", label: "Téli" },
  { id: "negyevszakos", label: "Négyévszakos" },
];

const WIDTHS = Array.from({ length: 19 }, (_, i) => 155 + i * 10);
const PROFILES = Array.from({ length: 15 }, (_, i) => 20 + i * 5);
const INCHES = Array.from({ length: 15 }, (_, i) => 10 + i);

const POPULAR_SIZES = ["195/65 R15", "205/55 R16", "225/45 R17", "245/40 R18"];

function Select({ testId, label, value, onChange, options, suffix = "" }) {
  return (
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-3 bg-gray-100/40 border border-gray-200 rounded-sm text-gray-900 focus:outline-none focus:border-violet-400 text-sm font-medium appearance-none cursor-pointer min-w-0"
    >
      <option value="" className="bg-gray-100">{label}</option>
      {options.map((o) => (
        <option key={o} value={String(o)} className="bg-gray-100">{o}{suffix}</option>
      ))}
    </select>
  );
}

export default function ShopSearch() {
  const [size, setSize] = useState("");
  const [width, setWidth] = useState("");
  const [profile, setProfile] = useState("");
  const [inch, setInch] = useState("");
  const [season, setSeason] = useState("all");

  const scrollToShop = () => {
    const el = document.getElementById("webshop");
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 70;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const w = width ? `${width}/` : "";
    const p = profile ? `${profile} ` : "";
    const i = inch ? `R${inch}` : "";
    const query = `${w}${p}${i}`.trim();
    setShopFilter({ size: query || size.trim(), season });
    scrollToShop();
  };

  const quickPick = (s) => {
    setSize(s);
    setShopFilter({ size: s, season });
    scrollToShop();
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="hero-shop-search"
      className="relative mt-10 border border-violet-300/70 bg-white p-4 sm:p-5 rounded-md shadow-[0_12px_45px_-10px_rgba(139,92,246,0.35),0_3px_12px_rgba(10,10,15,0.08)]"
    >
      <div className="absolute -top-px left-0 right-0 h-1 rounded-t-md bg-gradient-to-r from-blue-500 via-violet-500 to-blue-500" />

      <div className="flex items-center gap-2 mb-4">
        <Package className="w-4 h-4 text-violet-600" />
        <div className="text-[10px] uppercase tracking-[0.25em] text-violet-600 font-bold">
          Használt gumi raktárkereső
        </div>
      </div>

      {/* Dropdown méretválasztó */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 min-w-0">
        <Select testId="hero-search-width" label="Szélesség" value={width} onChange={setWidth} options={WIDTHS} />
        <Select testId="hero-search-profile" label="Magasság" value={profile} onChange={setProfile} options={PROFILES} />
        <Select testId="hero-search-inch" label="Col" value={inch} onChange={setInch} options={INCHES} suffix='"' />
      </div>

      <div className="my-3 flex items-center gap-3">
        <div className="h-px flex-1 bg-gray-100/10" />
        <span className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">vagy gyorskereső</span>
        <div className="h-px flex-1 bg-gray-100/10" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 min-w-0">
        <div className="relative min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            data-testid="hero-search-size"
            type="text"
            value={size}
            onChange={(e) => setSize(e.target.value)}
            placeholder='Méret pl. "205/55 R16"'
            className="w-full pl-10 pr-3 py-3 bg-gray-100/40 border border-gray-200 rounded-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-violet-400 text-sm font-medium"
          />
        </div>
        <select
          data-testid="hero-search-season"
          value={season}
          onChange={(e) => setSeason(e.target.value)}
          className="w-full px-3 py-3 bg-gray-100/40 border border-gray-200 rounded-sm text-gray-900 focus:outline-none focus:border-violet-400 text-sm font-medium appearance-none cursor-pointer min-w-0"
        >
          {SEASONS.map((s) => <option key={s.id} value={s.id} className="bg-gray-100">{s.label}</option>)}
        </select>
        <button
          data-testid="hero-search-submit"
          type="submit"
          className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 text-white font-bold uppercase tracking-wider text-xs rounded-sm transition-all glow-blue whitespace-nowrap"
        >
          Keresés <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Népszerű:</span>
        {POPULAR_SIZES.map((s) => (
          <button
            key={s}
            data-testid={`hero-quick-${s.replace(/[\s/]/g, "-")}`}
            type="button"
            onClick={() => quickPick(s)}
            className="px-2.5 py-1 text-[11px] font-mono text-violet-600 border border-violet-500/30 bg-violet-500/5 hover:bg-violet-500/15 rounded-sm transition"
          >
            {s}
          </button>
        ))}
      </div>
    </form>
  );
}
